/**
 * This is a redux-form/formik/etc - agnostic wrapper for adorning a field with label, tooltip,
 * error, etc.
 */
import commonPropTypes from '../../../constants/commonPropTypes'
import React, { Component, useState, useEffect } from 'react'
import { Form, Placeholder } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import classnames from 'classnames/bind'
import FieldError from './fieldError'
import FieldTooltip from './fieldTooltip'
import StandardFieldLabel from './standardFieldLabel'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const FIELD_PLACEHOLDER_LENGTHS = {
  SHORT: 'short',
  MEDIUM: 'medium',
  LONG: 'long',
  FULL: 'full',
}

class RenderEditMode extends Component {
  static propTypes = {
    render: PropTypes.func.isRequired,
    fieldProps: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)
    this.state = {
      fieldFocused: false,
    }
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      render,
      fieldProps: {
        touched,
        error,
        warning,
        label,
        checkboxLabel, // TO BE DEPRECATED
        helpContent,
        breakAll,
        hideLabel,
        hideWrapper,
        showTooltip,
        compact,
        relaxed,
        onFocus,
        onBlur,
        className,
        elmId,
        required,
        input,
        pendingApproval,
        disabled,
        footer: Footer,
        // not implemented for edit mode... but to omit from restProps
        isLoading,
        loadingPlaceholderLength,
        labelOnTop,
        borderBottom,
        normalFont,
        ...restProps
      },
    } = this.props

    const { fieldFocused } = this.state
    /* eslint-enable no-unused-vars */
    const showError = !!(touched && error)
    const showErrorAbove = showError && compact
    const showErrorBelow = showError && !compact
    const showAnchor = showTooltip || Boolean(helpContent)

    // determining if it's a checkbox.  "isChecked" is a deprecated prop that is considered here for backward compatibility.
    const isCheckbox =
      _.has(restProps, 'checked') || _.has(restProps, 'isChecked')

    const onFocusNew = e => {
      this.setState({ fieldFocused: true })
      onFocus && onFocus(e)
    }
    const onBlurNew = e => {
      this.setState({ fieldFocused: false })
      onBlur && onBlur(e)
    }

    const newDisabled = disabled || pendingApproval

    const labelProps = {
      compact,
      hideLabel,
      showAnchor,
      showErrorAbove,
      helpContent,
      error,
      warning,
      className,
      elmId,
      inputName: input?.name,
      required,
      fieldFocused,
      pendingApproval,
    }

    const checkboxLabelProps = {
      label,
      checkboxLabel,
      showAnchor,
      helpContent,
      pendingApproval,
    }

    return (
      <Form.Field
        error={showError}
        className={cx('field', { compact }, { relaxed })}
      >
        {/*
         * This standard label is for non-checkbox components (check box and radio buttons)
         * We can't reuse this without refactoring it such that the top element is a label, so instead we
         * pass in the checkboxLabel below in those cases.
         */}
        {!isCheckbox &&
          !hideWrapper &&
          (!hideLabel || showAnchor) &&
          StandardFieldLabel.createFromLabel(label, labelProps)}
        {render(
          {
            ...restProps,
            disabled: newDisabled,
            onFocus: onFocusNew,
            onBlur: onBlurNew,
            compact,
            className,
            elmId,
            required,
            focused: this.state.fieldFocused,
            ...(input ? { input } : {}),
            // props that we only send to checkboxes
            ...(isCheckbox ? checkboxLabelProps : {}),
          },
          showError
        )}
        {!hideWrapper && showErrorBelow && (
          <FieldError
            error={error}
            className={className}
            elmId={elmId}
            disabled={newDisabled}
            {...restProps}
          />
        )}
        {Footer && <Footer />}
      </Form.Field>
    )
  }
}

/**
 * Extracts the "view" version of the field based on how the data is held in value
 *
 * TODO - remove this logic and pass in a renderViewMode or something to let
 * the wrapped element control it's own view mode rendering.
 *
 * TODO - enhance logic for radio group fields to which currently only reflect the underlying value.  (Cross this bridge
 * when we get to it...)
 */
const getViewModeValue = fieldProps => {
  const { value, inputLabel, type, checked } = fieldProps
  const isCheckbox = _.has(fieldProps, 'checked')

  let result = ''
  if (fieldProps.options) {
    if (_.isArray(value)) {
      // dropdown
      const options = _.map(value, val => {
        const option = _.find(fieldProps.options, { value: val })
        return option ? option.text : ''
      })
      result = options ? options.join(', ') : ''
    } else {
      const option = _.find(fieldProps.options, { value })
      result = option ? option.text : ''
    }
  } else if (_.isObject(value) && value.selectedAddressText) {
    // address suggestion field
    result = value.selectedAddressText
  } else if (type === 'password') {
    result = '●'.repeat(value.length)
  } else if (isCheckbox) {
    result = checked ? 'Yes' : 'No'
  } else {
    const finalValue = _.isBoolean(value) ? String(value) : value

    // input, textarea, etc.
    result = result.replace(/\r/g, '<br>')
    result = finalValue ? `${inputLabel || ''}${finalValue || ''}` : ''
  }

  return result
}

const renderValue = (value, componentName) => {
  if (componentName === 'RichTextField') {
    return <span dangerouslySetInnerHTML={{ __html: value }} />
  }

  if (typeof value === 'string' && value.indexOf('\n') !== -1) {
    return value.split('\n').map((item, key) => {
      return (
        <span key={key}>
          {item}
          <br />
        </span>
      )
    })
  }

  return value || <span>{'\u00a0'}</span>
}

// lengths just used internally here - to send to underlying Placeholder component
const LENGTHS = {
  FULL: 'full',
  VERY_LONG: 'very long',
  LONG: 'long',
  MEDIUM: 'medium',
  SHORT: 'short',
  VERY_SHORT: 'very short',
}

/**
 * In determining desired length, always throw in some randomness relative to the desired length,
 * with the exception of FULL which is always FULL.
 *
 * NOTE: only randomize after mount - to avoid a server/client rendering mismatch
 * @param {enum} lengthRequested
 */
const getPlaceholderLength = (
  lengthRequested = FIELD_PLACEHOLDER_LENGTHS.MEDIUM,
  isMounted
) => {
  if (!isMounted) {
    return lengthRequested
  }

  const getRand = () => [Math.floor(Math.random() * 3)]
  let resultLength
  switch (lengthRequested) {
    case FIELD_PLACEHOLDER_LENGTHS.FULL:
      resultLength = LENGTHS.FULL
      break
    case FIELD_PLACEHOLDER_LENGTHS.SHORT:
      resultLength = [LENGTHS.VERY_SHORT, LENGTHS.SHORT, LENGTHS.MEDIUM][
        getRand()
      ]
      break
    case FIELD_PLACEHOLDER_LENGTHS.LONG:
      resultLength = [LENGTHS.MEDIUM, LENGTHS.LONG, LENGTHS.VERY_LONG][
        getRand()
      ]
      break
    default:
      resultLength = [LENGTHS.SHORT, LENGTHS.MEDIUM, LENGTHS.LONG][getRand()]
      break
  }
  return resultLength
}

const renderViewMode = (viewModeRenderer, fieldProps) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  })

  const {
    elmId,
    label,
    helpContent,
    breakAll,
    hideWrapper,
    hideLabel,
    labelOnTop = true,
    borderBottom,
    normalFont = false,
    showTooltip,
    compact,
    isLoading,
    loadingPlaceholderLength,
    componentName,
  } = fieldProps
  const value = getViewModeValue(fieldProps)
  const showAnchor = showTooltip // only show tooltip if specifically asked for (in view mode)
  const content = viewModeRenderer
    ? viewModeRenderer(fieldProps.value)
    : renderValue(value, componentName)

  return (
    <div
      className={cx(
        'view',
        { compact },
        { 'horizontal-layout': !labelOnTop },
        { 'normal-font': normalFont },
        { 'border-default': !borderBottom },
        { 'border-bottom-always': borderBottom === 'always' },
        { 'border-bottom-none': borderBottom === 'none' },
        { 'border-bottom-small-screens': borderBottom === 'smallScreensOnly' }
      )}
    >
      {!hideWrapper && !hideLabel && (
        <div data-elm-id={`${elmId}_label`} className={cx('label')}>
          {label}
        </div>
      )}
      <div
        data-elm-id={`${elmId}_value`}
        className={cx('value', { 'break-all': breakAll })}
      >
        {isLoading ? (
          <Placeholder className={cx('loading-placeholder')}>
            <Placeholder.Line
              length={getPlaceholderLength(loadingPlaceholderLength, isMounted)}
            />
          </Placeholder>
        ) : (
          content
        )}
      </div>
      {!hideWrapper && showAnchor && (
        <div className={cx('tooltip')}>
          <FieldTooltip helpContent={helpContent} {...fieldProps} />
        </div>
      )}
    </div>
  )
}

/**
 * formFieldWrapper essentially adorns a field with things like label, info and error UI...
 *
 * This is a wrapper function that takes as its input, a render function that is
 * responsible for rendering the inner input control.  That render function should accept a
 * param called 'fieldProps' that will be passed down from the caller.
 *
 * Practically speaking the wrapped function created by THIS function will be in turn wrapped
 * by redux-form's Field (passed as a 'component' to it). Redux-form's Field will deal with
 * passing down the field params accordingly.
 *
 * The props this HOC handles are:
 *  viewMode, viewModeRenderer, touched, error, helpContent, breakAll, inputLabel
 */
export const formFieldWrapper = render => fieldProps => {
  const { viewMode, viewModeRenderer, ...restProps } = fieldProps

  return viewMode ? (
    renderViewMode(viewModeRenderer, restProps)
  ) : (
    <RenderEditMode render={render} fieldProps={restProps} />
  )
}

export { commonPropTypes, getViewModeValue, FIELD_PLACEHOLDER_LENGTHS }
